// utilities/reverseGeocode.js
// ─────────────────────────────────────────────────────────────────────────────
// "0.3813715, 32.6511365" -> "Kisaasi Road, Kampala".
//
// Coordinates are the truth the delivery fee is measured against, but they are
// not something a dispatcher can read. This asks Google what is at the pin.
//
// EVERY LOOKUP IS BILLED, so this caches hard:
//   · in memory for the session, so re-rendering a screen costs nothing;
//   · in localStorage for 30 days, keyed by the coordinates rounded to 5
//     decimal places (about 1 metre — finer than any pin's own accuracy, so
//     two views of the same order always hit the same key);
//   · in-flight requests are shared, so a screen that mounts the same pin
//     twice issues ONE request rather than two.
//
// It resolves to null rather than throwing: a name is a convenience, and an
// order screen must never fail because a geocode did.
// ─────────────────────────────────────────────────────────────────────────────

const MEMORY = new Map();
const INFLIGHT = new Map();

const STORE_PREFIX = "lusuku.geo.";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days; roads do not move often

const keyFor = (lat, lng) =>
  `${Number(lat).toFixed(5)},${Number(lng).toFixed(5)}`;

const readStore = (key) => {
  try {
    const raw = window.localStorage.getItem(STORE_PREFIX + key);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - parsed.at > TTL_MS) return undefined;
    return parsed.name;
  } catch (err) {
    return undefined;
  }
};

const writeStore = (key, name) => {
  try {
    window.localStorage.setItem(
      STORE_PREFIX + key,
      JSON.stringify({ name, at: Date.now() })
    );
  } catch (err) {
    /* A cache that cannot be written is still a working lookup. */
  }
};

/**
 * Pick the most useful line out of a geocoder response.
 *
 * `results[0].formatted_address` is usually a plus-code-prefixed mouthful
 * ("2QP5+6G Kampala, Uganda"). A route or a neighbourhood reads far better to
 * someone briefing a rider, so those are preferred and the formatted address
 * is the fallback rather than the first choice.
 */
export const pickPlaceName = (results = []) => {
  if (!Array.isArray(results) || results.length === 0) return null;

  const byType = (type) =>
    results.find((r) => (r.types || []).includes(type))?.formatted_address;

  const route = results.find((r) => (r.types || []).includes("route"));
  if (route) {
    const road = route.address_components?.find((c) =>
      (c.types || []).includes("route")
    )?.long_name;
    const area = route.address_components?.find((c) =>
      (c.types || []).some((t) =>
        ["sublocality", "sublocality_level_1", "locality"].includes(t)
      )
    )?.long_name;
    if (road) return area ? `${road}, ${area}` : road;
  }

  const neighbourhood =
    byType("neighborhood") || byType("sublocality") || byType("locality");
  if (neighbourhood) return neighbourhood.replace(/^[A-Z0-9+]{4,}\s/, "");

  const first = results[0].formatted_address;
  // Strip a leading plus code, which carries no meaning for a human.
  return first ? first.replace(/^[A-Z0-9+]{4,}\s/, "") : null;
};

/**
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<string|null>} the place name, or null if it cannot be had
 */
export const reverseGeocode = (lat, lng) => {
  if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) {
    return Promise.resolve(null);
  }

  const key = keyFor(lat, lng);

  if (MEMORY.has(key)) return Promise.resolve(MEMORY.get(key));

  const stored = readStore(key);
  if (stored !== undefined) {
    MEMORY.set(key, stored);
    return Promise.resolve(stored);
  }

  if (INFLIGHT.has(key)) return INFLIGHT.get(key);

  const geocoder = window.google?.maps?.Geocoder
    ? new window.google.maps.Geocoder()
    : null;
  if (!geocoder) return Promise.resolve(null);

  const request = new Promise((resolve) => {
    geocoder.geocode(
      { location: { lat: Number(lat), lng: Number(lng) } },
      (results, status) => {
        const name = status === "OK" ? pickPlaceName(results) : null;
        if (name) {
          MEMORY.set(key, name);
          writeStore(key, name);
        }
        resolve(name);
      }
    );
  }).finally(() => INFLIGHT.delete(key));

  INFLIGHT.set(key, request);
  return request;
};

export default reverseGeocode;
